import {
  ActionComponent,
  DefrostScheduleConfig,
  RelatedDeviceSection,
} from '@repo/sdk/models/components';

import { extractDefrostScheduleRefs } from '@/features/equipment-browser/extract-refs';
import { extractRelatedDeviceRefs } from '@/features/equipment-browser/extract-refs';
import { mapSettingRefsToSubscriptionIdentifiers } from '@/features/equipment-browser/subscriptions/map-to-subscription-identifiers';
import { mapReadingRefsToSubscriptionIdentifiers } from '@/features/equipment-browser/subscriptions/map-to-subscription-identifiers';
import { extractActionComponentRefs } from '@/helpers/extract-refs';
import { SubscriptionIdentifier } from '@/hooks/query-hooks/gaia-api/use-subscription-base';

/**
 * Deduplicates subscription identifiers based on id and contextKey.
 * Identifiers with the same id and contextKey are considered duplicates.
 *
 * @param identifiers - Array of subscription identifiers to deduplicate
 * @returns Deduplicated array of subscription identifiers
 */
export const deduplicateSubscriptionIdentifiers = (
  identifiers: SubscriptionIdentifier[]
): SubscriptionIdentifier[] => {
  const seen = new Set<string>();
  return identifiers.filter((identifier) => {
    const key = `${identifier.id}:${identifier.contextKey ?? 'default'}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * getActionComponentIdentifiers
 * Extracts the action component identifiers from the action components
 *
 * @param actionComponents - The action components to extract the identifiers from
 * @param identifierType - The type of identifier to extract (setting or reading)
 * @returns The action component identifiers
 */
export const getActionComponentIdentifiers = (
  actionComponents: ActionComponent[] | undefined,
  isSetting: boolean
): SubscriptionIdentifier[] => {
  if (!actionComponents) return [];
  return mapSettingRefsToSubscriptionIdentifiers(
    extractActionComponentRefs(actionComponents, isSetting),
    isSetting ? 'actionComponentSettings' : 'actionComponentReadings'
  );
};

/**
 * getDefrostScheduleIdentifiers
 * Extracts the defrost schedule identifiers from the defrost schedule configuration
 *
 * @param defrostScheduleConfig - The defrost schedule configuration to extract the identifiers from
 * @param identifierType - The type of identifier to extract (setting or reading)
 * @returns The defrost schedule identifiers
 */
export const getDefrostScheduleIdentifiers = (
  defrostScheduleConfig: DefrostScheduleConfig | undefined,
  isSetting: boolean
): SubscriptionIdentifier[] => {
  if (!defrostScheduleConfig) return [];
  return mapSettingRefsToSubscriptionIdentifiers(
    extractDefrostScheduleRefs(defrostScheduleConfig, isSetting),
    isSetting ? 'defrostScheduleSettings' : 'defrostScheduleReadings'
  );
};

/**
 * getRelatedDevicesIdentifiers
 * Extracts the related devices identifiers from the related devices
 *
 * @param relatedDevices - The related devices to extract the identifiers from
 * @param identifierType - The type of identifier to extract (setting or reading)
 * @returns The related devices identifiers
 */
export const getRelatedDevicesIdentifiers = (
  relatedDevices: RelatedDeviceSection[] | undefined,
  isSetting: boolean
): SubscriptionIdentifier[] => {
  return mapReadingRefsToSubscriptionIdentifiers(
    extractRelatedDeviceRefs(relatedDevices, isSetting),
    isSetting ? 'relatedDeviceSettings' : 'relatedDeviceReadings'
  );
};
